import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AccountCategoryReference = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('account_category_references');
  const [viewItem, setViewItem] = useState(null);
  const [accountCategoryReferences, setAccountCategoryReferences] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReference, setEditingReference] = useState(null);

  useEffect(() => {
    loadAccountCategoryReferences();
  }, []);

  const loadAccountCategoryReferences = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/account-category-references`);
      setAccountCategoryReferences(response.data || []);
    } catch (error) {
      console.error('Error loading account category references:', error);
      toast.error('Failed to load account category references');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (reference = null) => {
    setEditingReference(reference);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      account_category_reference_id: (formData.get('account_category_reference_id') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingReference) {
        await axios.put(`${BACKEND_URL}/api/account-category-references/${editingReference.account_category_reference_id}`, {
          description: data.description
        });
        toast.success('Account Category Reference updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/account-category-references`, data);
        toast.success('Account Category Reference created successfully');
      }
      setIsDialogOpen(false);
      loadAccountCategoryReferences();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account category reference');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Account Category References</h1>
            <p className="text-sm text-gray-500 mt-1">Manage account category reference configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Account Category References</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Account Category Reference
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : accountCategoryReferences.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No account category references found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accountCategoryReferences.map((reference) => (
                    <tr key={reference.account_category_reference_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{reference.account_category_reference_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reference.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="account_category_references" onView={() => setViewItem(reference)} onEdit={() => handleOpenDialog(reference)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingReference ? 'Edit Account Category Reference' : 'Create Account Category Reference'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="account_category_reference_id">Account Category Reference ID * (4 characters)</Label>
                <Input
                  id="account_category_reference_id"
                  name="account_category_reference_id"
                  defaultValue={editingReference?.account_category_reference_id || ''}
                  disabled={editingReference !== null}
                  required
                  maxLength={4}
                  minLength={4}
                  pattern="[0-9]{4}"
                  title="Must be exactly 4 digits"
                  className="mt-1"
                  placeholder="e.g., 0009, 0010"
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Must be exactly 4 digits</p>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingReference?.description || ''}
                  required
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingReference ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default AccountCategoryReference;
